import { createSHA256 } from 'hash-wasm'

import { isTerminalWorkflowStatus, type ReviewStage } from '@maxus/common/domain'
import { trpcClient } from '@maxus/website/lib'

import { executeFrontendTaskRetry, getPendingFrontendTaskRetry } from './frontend-task-retry'

// 分块流式读文件算整文件 SHA-256（小写 hex）：避免把大文件整体读进内存。
// 与上传并发进行，结果随 finishUpload 上送，落到 biz_file.checksum（Astra 转码 source 必需）。
const HASH_CHUNK_SIZE = 8 * 1024 * 1024

const computeFileSha256 = async (file: File): Promise<string> => {
  const hasher = await createSHA256()
  hasher.init()
  for (let offset = 0; offset < file.size; offset += HASH_CHUNK_SIZE) {
    const chunk = file.slice(offset, Math.min(offset + HASH_CHUNK_SIZE, file.size))
    hasher.update(new Uint8Array(await chunk.arrayBuffer()))
  }
  return hasher.digest('hex')
}

type RegisterUploadInput = {
  deliverableId: string
  stageName: ReviewStage
  platformNames: string[]
  file: File
  sourceTimelineName?: string
  parentTimelineVersionId?: string
  timelineVersionId?: string
  note?: string
}

type MultipartUploadState = {
  attempt: number
  checksum: Promise<string>
  completedBytes: number
  file: File
  nextPartNumber: number
  partSize: number
  taskId: string
  uploadId: string
  workflowId: string
}

type ActiveUpload = {
  phase: 'uploading' | 'completing'
  request: XMLHttpRequest | null
  state: MultipartUploadState
  stopped: boolean
}

// File 引用只能保存在当前页面内存；刷新后由用户重新选择。
type RetryUploadParams = {
  file: File
}

const CURRENT_CLIENT_UPLOADS_KEY = 'maxus_upload_workflows'
const DEFAULT_MIME_TYPE = 'application/octet-stream'

type UploadFileParams = {
  filename: string
  mimeType: string
  sizeBytes: number
}

type CachedUploadWorkflow = {
  attempt: number
  file?: UploadFileParams
  interrupted?: true
  taskId: string
  uploadId?: string
  workflowId: string
}

type CachedUploadWorkflows = {
  records: CachedUploadWorkflow[]
  version: 1
}

const parseUploadFileParams = (value: unknown): UploadFileParams | undefined => {
  if (typeof value !== 'object' || value === null) return
  const file = value as Record<string, unknown>
  if (typeof file.filename !== 'string' || typeof file.mimeType !== 'string' || typeof file.sizeBytes !== 'number')
    return
  return {
    filename: file.filename,
    mimeType: file.mimeType,
    sizeBytes: file.sizeBytes,
  }
}

const parseCachedUploadWorkflow = (value: unknown): CachedUploadWorkflow | undefined => {
  if (typeof value !== 'object' || value === null) return

  const record = value as Record<string, unknown>
  if (typeof record.workflowId !== 'string' || typeof record.taskId !== 'string' || typeof record.attempt !== 'number')
    return

  const file = parseUploadFileParams(record.file)

  return {
    attempt: record.attempt,
    ...(file ? { file } : {}),
    ...(record.interrupted === true ? { interrupted: true as const } : {}),
    taskId: record.taskId,
    ...(typeof record.uploadId === 'string'
      ? { uploadId: record.uploadId }
      : {}),
    workflowId: record.workflowId,
  }
}

const readCachedUploadWorkflows = (): CachedUploadWorkflow[] => {
  try {
    const cached: unknown = JSON.parse(localStorage.getItem(CURRENT_CLIENT_UPLOADS_KEY) ?? 'null')
    const values =
      typeof cached === 'object' && cached !== null && Array.isArray((cached as CachedUploadWorkflows).records)
        ? (cached as CachedUploadWorkflows).records
        : []
    return values.flatMap(value => {
      const record = parseCachedUploadWorkflow(value)
      return record ? [record] : []
    })
  } catch {
    return []
  }
}

const writeCachedUploadWorkflows = (records: CachedUploadWorkflow[]) => {
  const cached: CachedUploadWorkflows = { records, version: 1 }
  try {
    localStorage.setItem(CURRENT_CLIENT_UPLOADS_KEY, JSON.stringify(cached))
  } catch {
    // localStorage 不可用（隐私浏览、存储已满等）。
  }
}

const cacheUploadWorkflow = (record: CachedUploadWorkflow) => {
  const records = readCachedUploadWorkflows()
  const index = records.findIndex(({ workflowId }) => workflowId === record.workflowId)
  if (index === -1) records.push(record)
  else records[index] = records[index].attempt === record.attempt ? { ...records[index], ...record } : record
  writeCachedUploadWorkflows(records)
}

const markCachedUploadInterrupted = (workflowId: string, attempt: number) => {
  const records = readCachedUploadWorkflows()
  const index = records.findIndex(record => record.workflowId === workflowId && record.attempt === attempt)
  if (index === -1) return
  const record = records[index]
  records[index] = {
    attempt: record.attempt,
    ...(record.file
      ? { file: record.file }
      : {}),
    interrupted: true,
    taskId: record.taskId,
    workflowId: record.workflowId,
  }
  writeCachedUploadWorkflows(records)
}

const clearCachedUploadWorkflow = (workflowId: string, attempt: number) => {
  const records = readCachedUploadWorkflows()
  const remaining = records.filter(record => record.workflowId !== workflowId || record.attempt !== attempt)
  if (remaining.length === records.length) return
  writeCachedUploadWorkflows(remaining)
}

export class UploadManager {
  private initializePromise: Promise<void> | null = null
  private activeUploads = new Map<string, ActiveUpload>()
  private pausedUploads = new Map<string, MultipartUploadState>()
  private retryPromises = new Map<string, Promise<void>>()
  // workflowId → 重试参数（失败/取消后按此重发）。
  private retryable = new Map<string, RetryUploadParams>()

  async register({
    deliverableId,
    stageName,
    platformNames,
    file,
    sourceTimelineName,
    parentTimelineVersionId,
    timelineVersionId,
    note,
  }: RegisterUploadInput) {
    await this.initialize()
    const uploads = await trpcClient.project.asset.createUpload.mutate({
      deliverableId,
      stageName,
      platformNames,
      sourceTimelineName,
      parentTimelineVersionId,
      timelineVersionId,
      note,
      filename: file.name,
      mimeType: file.type || DEFAULT_MIME_TYPE,
      sizeBytes: file.size,
    })

    const uploadFile: UploadFileParams = {
      filename: file.name,
      mimeType: file.type || DEFAULT_MIME_TYPE,
      sizeBytes: file.size,
    }

    uploads.forEach(({ attempt, taskId, workflowId }) => {
      cacheUploadWorkflow({
        attempt,
        file: uploadFile,
        taskId,
        workflowId,
      })
    })

    // 同一文件会并行传到各 platform 的落点，内容相同、checksum 一致，只算一次共享给各 finishUpload。
    const checksum = computeFileSha256(file)

    uploads.forEach(({ workflowId }) => {
      this.retryable.set(workflowId, { file })
    })

    const states: MultipartUploadState[] = []
    try {
      for (const { attempt, workflowId, taskId } of uploads) {
        const { partSize, uploadId } = await trpcClient.project.asset.createMultipartUpload.mutate({
          attempt,
          taskId,
        })
        cacheUploadWorkflow({
          attempt,
          taskId,
          uploadId,
          workflowId,
        })
        states.push({
          attempt,
          checksum,
          completedBytes: 0,
          file,
          nextPartNumber: 1,
          partSize,
          taskId,
          uploadId,
          workflowId,
        })
      }
    } catch (error) {
      await Promise.allSettled(
        states.map(({ attempt, taskId, uploadId }) =>
          trpcClient.project.asset.abortMultipartUpload.mutate({ attempt, taskId, uploadId }),
        ),
      )
      await Promise.allSettled(
        uploads.map(async ({ attempt, taskId, workflowId }) => {
          await trpcClient.project.task.update.mutate({ taskId, attempt, status: 'retry-waiting' })
          markCachedUploadInterrupted(workflowId, attempt)
        }),
      )
      throw error
    }

    states.forEach(state => this.startUpload(state))
  }

  private startUpload(state: MultipartUploadState) {
    const activeUpload: ActiveUpload = {
      phase: 'uploading',
      request: null,
      state,
      stopped: false,
    }
    this.activeUploads.set(state.workflowId, activeUpload)

    void this
      .uploadParts(activeUpload)
      .catch(() => undefined)
      .finally(() => {
        if (this.activeUploads.get(state.workflowId) === activeUpload) {
          this.activeUploads.delete(state.workflowId)
        }
      })
  }

  private async uploadParts(activeUpload: ActiveUpload) {
    const { state } = activeUpload
    let lastProgress = state.file.size === 0 ? 0 : Math.floor((state.completedBytes / state.file.size) * 100)
    let progressUpdate = Promise.resolve()

    try {
      const partCount = Math.max(1, Math.ceil(state.file.size / state.partSize))
      while (state.nextPartNumber <= partCount) {
        if (activeUpload.stopped) return

        const partNumber = state.nextPartNumber
        const start = (partNumber - 1) * state.partSize
        const part = state.file.slice(start, Math.min(start + state.partSize, state.file.size))
        const { presignedPutUrl } = await trpcClient.project.asset.createUploadPartUrl.mutate({
          attempt: state.attempt,
          partNumber,
          taskId: state.taskId,
          uploadId: state.uploadId,
        })
        if (activeUpload.stopped) return

        const request = new XMLHttpRequest()
        activeUpload.request = request
        try {
          await this.uploadPart(request, presignedPutUrl, part)
        } finally {
          if (activeUpload.request === request) activeUpload.request = null
        }
        if (activeUpload.stopped) return

        state.completedBytes += part.size
        state.nextPartNumber += 1
        const progress =
          state.file.size === 0 ? 99 : Math.min(99, Math.floor((state.completedBytes / state.file.size) * 100))
        if (progress > lastProgress) {
          lastProgress = progress
          progressUpdate = progressUpdate
            .catch(() => undefined)
            .then(() =>
              trpcClient.project.task.update.mutate({
                taskId: state.taskId,
                attempt: state.attempt,
                status: 'processing',
                progress,
              }),
            )
            .then(() => undefined)
        }
      }

      await progressUpdate.catch(() => undefined)
      if (activeUpload.stopped) return
      activeUpload.phase = 'completing'
      await trpcClient.project.asset.completeMultipartUpload.mutate({
        attempt: state.attempt,
        taskId: state.taskId,
        uploadId: state.uploadId,
      })
      if (activeUpload.stopped) return

      // checksum 尽力而为：算不出（读文件失败等）就不带，仅使转码降级，不拖垮上传本身。
      const resolvedChecksum = await state.checksum.catch(() => undefined)
      if (activeUpload.stopped) return
      await trpcClient.project.asset.finishUpload.mutate({
        taskId: state.taskId,
        attempt: state.attempt,
        checksum: resolvedChecksum,
      })
      clearCachedUploadWorkflow(state.workflowId, state.attempt)
      // 成功即无需重试，释放 File 引用。
      this.retryable.delete(state.workflowId)
    } catch (error) {
      await progressUpdate.catch(() => undefined)
      if (activeUpload.stopped) return
      await trpcClient.project.asset.abortMultipartUpload
        .mutate({ attempt: state.attempt, taskId: state.taskId, uploadId: state.uploadId })
        .catch(() => undefined)
      const reported = await trpcClient.project.task.update
        .mutate({
          taskId: state.taskId,
          attempt: state.attempt,
          status: 'retry-waiting',
        })
        .then(
          () => true,
          () => false,
        )
      if (reported) markCachedUploadInterrupted(state.workflowId, state.attempt)
      throw error
    }
  }

  private uploadPart(request: XMLHttpRequest, url: string, part: Blob) {
    return new Promise<void>((resolve, reject) => {
      request.open('PUT', url)
      request.addEventListener(
        'load',
        () => {
          if (request.status >= 200 && request.status < 300) resolve()
          else reject(new Error(`Upload part failed with status ${request.status}`))
        },
      )
      request.addEventListener(
        'error',
        () => reject(new Error('Upload part failed')),
      )
      request.addEventListener(
        'abort',
        () => reject(new Error('Upload part canceled')),
      )
      request.send(part)
    })
  }

  getPendingRetry(workflowId: string) {
    return getPendingFrontendTaskRetry(workflowId, 'upload')
  }

  initialize() {
    if (!this.initializePromise) {
      window.addEventListener('beforeunload', this.reportInterruptedUploads)
      this.initializePromise = this.restoreCachedUploads()
    }
    return this.initializePromise
  }

  private reportInterruptedUploads = () => {
    for (const { attempt, interrupted, taskId } of readCachedUploadWorkflows()) {
      if (interrupted) continue
      void trpcClient.project.task.update
        .mutate({ taskId, attempt, status: 'retry-waiting' })
        .catch(() => undefined)
    }
  }

  private async restoreCachedUploads() {
    await Promise.allSettled(
      readCachedUploadWorkflows().map(async cached => {
        try {
          const workflow = await trpcClient.project.workflow.get.query({ workflowId: cached.workflowId })
          if (isTerminalWorkflowStatus(workflow.status)) {
            clearCachedUploadWorkflow(cached.workflowId, cached.attempt)
            return
          }
          if (cached.interrupted) return

          if (cached.uploadId) {
            await trpcClient.project.asset.abortMultipartUpload
              .mutate({
                attempt: cached.attempt,
                taskId: cached.taskId,
                uploadId: cached.uploadId,
              })
              .catch(() => undefined)
          }
          await trpcClient.project.task.update.mutate({
            taskId: cached.taskId,
            attempt: cached.attempt,
            status: 'retry-waiting',
          })
          markCachedUploadInterrupted(cached.workflowId, cached.attempt)
        } catch (error) {
          console.error('[upload-manager] failed to restore cached upload', error)
        }
      }),
    )
  }

  retry(workflowId: string, taskId: string, expectedAttempt: number): Promise<void> {
    const existing = this.retryPromises.get(workflowId)
    if (existing) return existing

    const promise = this.retryOnce(workflowId, taskId, expectedAttempt)
    this.retryPromises.set(workflowId, promise)
    const clear = () => {
      if (this.retryPromises.get(workflowId) === promise) this.retryPromises.delete(workflowId)
    }
    void promise.then(clear, clear)
    return promise
  }

  private async retryOnce(workflowId: string, taskId: string, expectedAttempt: number) {
    let params = this.retryable.get(workflowId)
    if (!params) {
      const cachedFile = readCachedUploadWorkflows().find(record => record.workflowId === workflowId)?.file
      const file = await this.selectRetryFile(cachedFile?.mimeType)
      if (!file) return
      const expectedFile = cachedFile ?? (await this.getExpectedUploadFile(workflowId, taskId))
      if (
        file.name !== expectedFile.filename ||
        file.size !== expectedFile.sizeBytes ||
        (file.type || DEFAULT_MIME_TYPE) !== expectedFile.mimeType
      ) {
        throw new Error('The selected file does not match the original upload.')
      }
      params = { file }
      this.retryable.set(workflowId, params)
    }
    const result = await executeFrontendTaskRetry({
      workflowId,
      taskId,
      expectedAttempt,
      taskType: 'upload',
    })
    if (result.taskType !== 'upload' || !result.setup) {
      throw new Error('Upload retry did not return setup')
    }

    cacheUploadWorkflow({
      attempt: result.attempt,
      file: {
        filename: params.file.name,
        mimeType: params.file.type || DEFAULT_MIME_TYPE,
        sizeBytes: params.file.size,
      },
      taskId,
      workflowId,
    })

    try {
      const { partSize, uploadId } = await trpcClient.project.asset.createMultipartUpload.mutate({
        attempt: result.attempt,
        taskId,
      })
      cacheUploadWorkflow({
        attempt: result.attempt,
        taskId,
        uploadId,
        workflowId,
      })
      this.startUpload({
        attempt: result.attempt,
        checksum: computeFileSha256(params.file),
        completedBytes: 0,
        file: params.file,
        nextPartNumber: 1,
        partSize,
        taskId,
        uploadId,
        workflowId,
      })
    } catch (error) {
      const reported = await trpcClient.project.task.update
        .mutate({
          taskId,
          attempt: result.attempt,
          status: 'retry-waiting',
          error: error instanceof Error ? error.message : String(error),
        })
        .then(
          () => true,
          () => false,
        )
      if (reported) markCachedUploadInterrupted(workflowId, result.attempt)
      throw error
    }
  }

  private selectRetryFile(mimeType?: string) {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = mimeType && mimeType !== DEFAULT_MIME_TYPE ? mimeType : 'video/*'

    return new Promise<File | undefined>(resolve => {
      let settled = false
      let focusTimer: number | undefined
      const settle = (file?: File) => {
        if (settled) return
        settled = true
        if (focusTimer !== undefined) window.clearTimeout(focusTimer)
        window.removeEventListener('focus', handleFocus)
        resolve(file)
      }
      const handleFocus = () => {
        // 部分浏览器不派发 input cancel；稍后检查，避免 focus 早于 change 时误判为取消。
        focusTimer = window.setTimeout(
          () => settle(input.files?.[0]),
          300,
        )
      }
      input.addEventListener(
        'change',
        () => settle(input.files?.[0]),
        { once: true },
      )
      input.addEventListener(
        'cancel',
        () => settle(),
        { once: true },
      )
      window.addEventListener('focus', handleFocus, { once: true })
      input.click()
    })
  }

  private async getExpectedUploadFile(workflowId: string, taskId: string) {
    const tasks = await trpcClient.project.task.listByWorkflow.query({ workflowId })
    const task = tasks.find(item => item.id === taskId && item.type === 'upload')
    const file = parseUploadFileParams(task?.jobConfig?.file)
    if (!file) throw new Error('The original upload parameters are unavailable.')
    return file
  }

  canPause(workflowId: string) {
    const activeUpload = this.activeUploads.get(workflowId)
    return activeUpload?.phase === 'uploading' && !activeUpload.stopped
  }

  isPaused(workflowId: string) {
    return this.pausedUploads.has(workflowId)
  }

  // 暂停只中止当前 part；已完成 part 保留在 multipart session，继续时从当前 part 重传。
  pause(workflowId: string) {
    const activeUpload = this.activeUploads.get(workflowId)
    if (!activeUpload || activeUpload.phase !== 'uploading' || activeUpload.stopped) return false
    activeUpload.stopped = true
    this.pausedUploads.set(workflowId, activeUpload.state)
    activeUpload.request?.abort()
    return true
  }

  resume(workflowId: string) {
    const state = this.pausedUploads.get(workflowId)
    if (!state) return false
    this.pausedUploads.delete(workflowId)
    this.startUpload(state)
    return true
  }

  // 中止当前 part，并终止远端 multipart session，释放已上传的 parts。
  async abort(workflowId: string) {
    const pausedState = this.pausedUploads.get(workflowId)
    this.pausedUploads.delete(workflowId)
    const activeUpload = this.activeUploads.get(workflowId)
    if (activeUpload) {
      activeUpload.stopped = true
      activeUpload.request?.abort()
    }
    const state = pausedState ?? activeUpload?.state
    if (!state) return
    await trpcClient.project.asset.abortMultipartUpload.mutate({
      attempt: state.attempt,
      taskId: state.taskId,
      uploadId: state.uploadId,
    })
  }
}

export const uploadManager = new UploadManager()
